import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { auth, db } from './firebaseConfig';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import MainApp from './MainApp';
// import OrderPage from './OrderPage';
import OrderWrapper from "./OrderWrapper";
import RecipePage from './RecipePage';
import SearchOrderPage from './SearchOrderPage';
import SalesReportPage from './SalesReportPage';
import recipes from './recipesData';
import syrups from './syrupsData';
import { signOut } from 'firebase/auth';
import 'antd/dist/reset.css';
import './App.css';
import Logo from "./assets/images/LOGO with name LIGHT.png";
import AdminUsers from './adminUsers';
import logUserAction from './utils/logUserAction.js';
import ProtectedRoute from "./components/ProtectedRoute"; // adjust path if needed
import AdminLogsPage from "./AdminLogsPage";
import AdminInventoryPage from './AdminInventoryPage';

function AppWrapper() {
  return (
    <Router>
      <App />
    </Router>
  );
}

function App() {
  const navigate = useNavigate();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [hasSpecialAccess, setHasSpecialAccess] = useState(false);
  const [userStore, setUserStore] = useState("");
  const [selectedStore, setSelectedStore] = useState("");
  const [authChecked, setAuthChecked] = useState(false); // ✅
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);


  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (currentUser) {
        const userDocRef = doc(db, "users", currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (!userDocSnap.exists()) {
          setUser(null);
          setIsAdmin(false);
          setIsApproved(false);
          setHasSpecialAccess(false);
          navigate('/');
          setAuthChecked(true);
          return;
        }

        const userData = userDocSnap.data();

        // ✅ Update last login
        await updateDoc(userDocRef, {
          lastLogin: serverTimestamp(),
        });

        // ✅ Log the login only once here
        await logUserAction(
          {
            ...currentUser,
            email: currentUser.email,
            uid: currentUser.uid,
          },
          "Logged in"
        );

        const role = (userData.role || "").toLowerCase();

        if (userData.admin === true) {
          setUser({
            ...currentUser,
            role: "admin",
            store: userData.store || '',
            approved: userData.approved,
            specialAccess: userData.specialAccess,
          });
          setIsAdmin(true);
        } else if (role === "owner" && userData.approved) {
          setUser({
            ...currentUser,
            role: "owner",
            store: userData.store || '',
            approved: true,
            specialAccess: userData.specialAccess || false,
          });
          setSelectedStore(userData.store);
        } else if (role === "staff" && userData.approved) {
          setUser({
            ...currentUser,
            role: "staff",
            store: userData.store || '',
            approved: true,
            specialAccess: userData.specialAccess || false,
          });
        } else {
          setUser(null);
        }

        setIsApproved(userData?.approved || false);
        setHasSpecialAccess(userData?.specialAccess || false);
        setUserStore(userData?.store || "No Store Assigned");
      } else {
        setUser(null);
        setIsAdmin(false);
        setIsApproved(false);
        setHasSpecialAccess(false);
        navigate('/');
      }

      setAuthChecked(true);
    });

    return () => unsubscribe();
  }, [navigate]);

  // ✅ Online/offline status already fine
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    setUser(null);
    setIsAdmin(false);
    navigate('/');
  };

  // ✅ Mask everything until auth check is complete!
  if (!authChecked) {
    return (
      <div className="flex items-center justify-center h-screen bg-white z-50">
        <div className="text-center">
          <p className="text-lg text-gray-700 mb-2">Loading...</p>
          {/* Add a spinner if you want */}
          <div className="border-4 border-blue-500 border-t-transparent rounded-full w-8 h-8 animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      {/* Offline Banner */}
      {!isOnline && (
        <div className="fixed top-0 left-0 w-full bg-red-600 text-white text-center py-1 z-50">
          Offline Mode: Orders cannot be submitted
        </div>
      )}

      {/* Navbar always visible */}
      <Navbar
        user={user}
        isAdmin={isAdmin}
        isApproved={isApproved}
        hasSpecialAccess={hasSpecialAccess}
        userStore={userStore}
        handleLogout={handleLogout}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      {/* App Content */}
      <div className="pt-[60px] p-4 relative z-0">
        <Routes>
          <Route path="/" element={<MainApp />} />

          <Route path="/order" element={<OrderWrapper />} />

          {isApproved && (
            <Route path="/search-order" element={<SearchOrderPage />} />
          )}

          {hasSpecialAccess && (
            <Route path="/recipes" element={<RecipePage recipes={recipes} syrups={syrups} />} />
          )}

          {user?.email === "brian@nieckarz.ca" && isAdmin && (
            <Route path="/admin-users" element={<AdminUsers />} />
          )}

          <Route
            path="/admin-logs"
            element={
              <ProtectedRoute>
                <AdminLogsPage />
              </ProtectedRoute>
            }
          />

          {isAdmin && <Route path="/admin-inventory" element={<AdminInventoryPage user={user} />} />}

          {(isAdmin || user?.role?.toLowerCase() === "owner") && (
            <Route
              path="/sales-report"
              element={
                <SalesReportPage
                  user={user}
                  isAdmin={isAdmin}
                  userStore={userStore}
                  selectedStore={selectedStore}
                  setSelectedStore={setSelectedStore}
                  isOnline={isOnline}
                />
              }
            />
          )}

          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </div>

      {/* Fullscreen Loading Overlay */}
      {!authChecked && (
        <div className="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50">
          <div className="text-center">
            <p className="text-lg text-gray-700 mb-4">Loading...</p>
            <div className="border-4 border-blue-500 border-t-transparent rounded-full w-12 h-12 animate-spin mx-auto"></div>
          </div>
        </div>
      )}
    </div>
  );
}

function Navbar({ user, isAdmin, isApproved, hasSpecialAccess, userStore, handleLogout, mobileMenuOpen, setMobileMenuOpen }) {
  const location = useLocation();
  const selectorRef = useRef(null);
  const navRef = useRef(null);

  const moveSelector = () => {
    const activeItem = navRef.current?.querySelector('.active');
    const selector = selectorRef.current;

    if (activeItem && selector) {
      selector.style.display = 'block';
      selector.style.top = `${activeItem.offsetTop}px`;
      selector.style.left = `${activeItem.offsetLeft}px`;
      selector.style.width = `${activeItem.offsetWidth}px`;
      selector.style.height = `${activeItem.offsetHeight}px`;
    } else if (selector) {
      selector.style.display = 'none';
    }
  };

  useEffect(() => {
    // Wait a bit before running, to let layout settle
    const timeout = setTimeout(() => {
      requestAnimationFrame(moveSelector); // use the one defined above
    }, 50);

    window.addEventListener('resize', moveSelector);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('resize', moveSelector);
    };
  }, [location]);


  const isActive = (path) => location.pathname === path;

  // ✅ Check if user has access to see nav links and logout button
  const hasAccess = isApproved || isAdmin || hasSpecialAccess;

  return (
    <>
      <nav className="navbar-mainbg flex justify-between items-center fixed top-0 left-0 w-full z-40">
        {/* ✅ Always Show Logo */}
        <div className="navbar-logo ml-4">
          <Link to="/">
            <img
              src={Logo}
              alt="TIFSU Logo"
              className="h-10 object-contain"
            />
          </Link>
        </div>

        {/* ✅ Show Navigation Links Only if user has access */}
        {hasAccess && (
          <div className="hidden lg:flex justify-center relative flex-grow" ref={navRef}>
            <ul className="flex h-[60px] items-center lg:flex-row flex-col lg:space-x-4 space-y-0 lg:space-y-0 text-white text-sm font-medium px-6 pt-0 mb-0">
              <div className="hori-selector absolute" ref={selectorRef}>
                <div className="left"></div>
                <div className="right"></div>
              </div>

              {isApproved && (
                <li className={`nav-item ${isActive('/order') ? 'active' : ''}`}>
                  <Link className="nav-link flex items-center gap-x-2" to="/order">
                    <i className="fas fa-tachometer-alt"></i>
                    <span>Order</span>
                  </Link>
                </li>
              )}
              {isApproved && (
                <li className={`nav-item ${isActive('/search-order') ? 'active' : ''}`}>
                  <Link className="nav-link flex items-center gap-x-2" to="/search-order">
                    <i className="fas fa-search"></i>
                    <span>Search Order</span>
                  </Link>
                </li>
              )}
              {hasSpecialAccess && (
                <li className={`nav-item ${isActive('/recipes') ? 'active' : ''}`}>
                  <Link className="nav-link flex items-center gap-x-2" to="/recipes">
                    <i className="far fa-address-book"></i>
                    <span>Recipes</span>
                  </Link>
                </li>
              )}
              {user?.email === "brian@nieckarz.ca" && (
                <li className={`nav-item ${isActive('/admin-users') ? 'active' : ''}`}>
                  <Link className="nav-link flex items-center gap-x-2" to="/admin-users">
                    <i className="fas fa-users-cog"></i>
                    <span>Users</span>
                  </Link>
                </li>
              )}
              {isAdmin && (
                <li className={`nav-item ${isActive('/admin-inventory') ? 'active' : ''}`}>
                  <Link className="nav-link flex items-center gap-x-2" to="/admin-inventory">
                    <i className="fas fa-boxes"></i>
                    <span>Inventory</span>
                  </Link>
                </li>
              )}
              {(isAdmin || user?.role === "Owner") && (
                <li className={`nav-item ${isActive('/sales-report') ? 'active' : ''}`}>
                  <Link className="nav-link flex items-center gap-x-2" to="/sales-report">
                    <i className="far fa-chart-bar"></i>
                    <span>Analytics</span>
                  </Link>
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Hamburger Icon */}
        <div className="lg:hidden pr-8">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-white focus:outline-none"
          >
            <div className="relative w-6 h-5" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              <span
                className={`absolute h-0.5 w-6 bg-white transition duration-300 ease-in-out transform ${mobileMenuOpen ? 'rotate-45 top-2.5' : 'top-0'
                  }`}
              ></span>
              <span
                className={`absolute h-0.5 w-6 bg-white transition-opacity duration-300 ease-in-out ${mobileMenuOpen ? 'opacity-0' : 'top-2.5'
                  }`}
              ></span>
              <span
                className={`absolute h-0.5 w-6 bg-white transition duration-300 ease-in-out transform ${mobileMenuOpen ? '-rotate-45 top-2.5' : 'top-5'
                  }`}
              ></span>
            </div>
          </button>
        </div>

        {/* Right-side info */}
        {hasAccess ? (
          <div className="hidden lg:flex items-center space-x-4 mr-4">
            <div className="flex flex-col text-right">
              <span className="text-white text-sm">{user?.email}</span>
              <span className="text-gray-300 text-xs">
                {isAdmin ? "Admin" : userStore}
              </span>
            </div>

            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-red-500 text-white rounded-lg"
            >
              Logout
            </button>
          </div>
        ) : (
          <div className="mr-4"></div>
        )}
      </nav>

      {/* ✅ Mobile Menu Overlay */}
      <div className={`fixed top-[60px] left-0 w-full h-[calc(100vh-60px)] z-30 bg-black transition-all duration-300 ${mobileMenuOpen ? 'bg-opacity-50 pointer-events-auto' : 'bg-opacity-0 pointer-events-none'}`}>
        <div
          className={`fixed top-[60px] right-0 h-[calc(100vh-60px)] w-full z-40 bg-white transform transition-transform duration-300 ease-in-out ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`} >

          <ul className="space-y-4 text-left text-lg font-medium text-[#5161ce] px-6 pt-4">
            {isApproved && (
              <li>
                <Link to="/order" onClick={() => setMobileMenuOpen(false)}>
                  Order
                </Link>
              </li>
            )}
            {isApproved && (
              <li>
                <Link to="/search-order" onClick={() => setMobileMenuOpen(false)}>
                  Search Order
                </Link>
              </li>
            )}
            {hasSpecialAccess && (
              <li>
                <Link to="/recipes" onClick={() => setMobileMenuOpen(false)}>
                  Recipes
                </Link>
              </li>
            )}
            {user?.email === "brian@nieckarz.ca" && (
              <li>
                <Link to="/admin-users" onClick={() => setMobileMenuOpen(false)}>
                  Users
                </Link>
              </li>
            )}
            {(isAdmin || user?.role === "Owner") && (
              <li>
                <Link to="/sales-report" onClick={() => setMobileMenuOpen(false)}>
                  Analytics
                </Link>
              </li>
            )}
            <li>
              <button
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                className="text-red-500"
              >
                Logout
              </button>
            </li>
          </ul>
          <div className="px-6 py-4 border-t text-sm text-gray-600">
            <div className="font-medium text-black">{user?.email}</div>
            <div className="text-gray-500">{isAdmin ? "Admin" : userStore}</div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AppWrapper;
